import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Eye, Plus, Edit, Loader2, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { listSites, createSite, updateSite } from '@/app/services/siteService';
import { SiteCreate, SiteUpdate, Site, ListQueryParams } from '@/app/types';
import SiteForm from '@/app/components/forms/SiteForm';
import { useAuthStore } from '@/app/store/authStore';
import { Link } from 'react-router-dom';
import { UserProfile } from '@/app/types/auth';

// Define selectors outside component to prevent recreation on each render
const selectUser = (state: { user: UserProfile | null }): UserProfile | null => state.user;
const selectLogoutAction = (state: { logoutAction: () => void }): () => void => state.logoutAction;
const selectIsLoading = (state: { isLoading: boolean }): boolean => state.isLoading;

const SitesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [currentSite, setCurrentSite] = useState<Site | null>(null);
  
  // Use individual selectors to prevent unnecessary rerenders
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Check for organization ID in either organization_id property or nested organization.id
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  
  // Get embedded sites data from user object if available
  const userSites = useMemo(() => user?.sites || [], [user]);
  
  const isOrgAdmin = user?.role?.name === 'org_admin';
  
  // For debugging
  useEffect(() => {
    console.log('SitesPage mounted, user:', user);
    console.log('User organization ID:', userOrgId);
    console.log('User embedded sites:', userSites);
  }, [user, userOrgId, userSites]);

  // Removed pre-population to always fetch fresh data from API

  // Query to fetch sites for the user's organization
  const { 
    data: sitesResponse, 
    isLoading: isSitesLoading, 
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['sites', userOrgId],
    queryFn: () => {
      console.log('Executing sites query with orgId:', userOrgId);
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      const params: ListQueryParams = { 
        page: 1, 
        page_size: 100
      };
      return listSites(params, userOrgId);
    },
    enabled: !!userOrgId, // Always fetch fresh data from API
    staleTime: 0, // Always consider data stale to force refetch
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Always use fresh data from API query
  const sites = useMemo(() => {
    if (sitesResponse) return sitesResponse;
    return { items: [], total: 0, page: 1, page_size: 10, pages: 0 };
  }, [sitesResponse]);

  // Log errors for debugging
  useEffect(() => {
    if (isError) {
      console.error('Error fetching sites:', error);
    }
  }, [isError, error]);

  // Mutation for creating a site
  const createMutation = useMutation({
    mutationFn: (data: SiteCreate) => createSite(data, userOrgId),
    onSuccess: () => {
      toast.success('Site created successfully');
      queryClient.invalidateQueries({ queryKey: ['sites', userOrgId] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create site: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Mutation for updating a site
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SiteUpdate }) => updateSite(id, data),
    onSuccess: () => {
      toast.success('Site updated successfully');
      queryClient.invalidateQueries({ queryKey: ['sites', userOrgId] });
      setIsEditFormOpen(false);
      setCurrentSite(null);
    },
    onError: (error) => {
      toast.error(`Failed to update site: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const handleCreateClick = (): void => {
    setIsCreateFormOpen(true);
  };

  const handleEditClick = (site: Site): void => {
    setCurrentSite(site);
    setIsEditFormOpen(true);
  };

  const handleCreateSubmit = (formData: SiteCreate | SiteUpdate): void => {
    createMutation.mutate(formData as SiteCreate);
  };

  const handleEditSubmit = (formData: SiteUpdate): void => {
    if (currentSite) {
      updateMutation.mutate({ id: currentSite.id, data: formData });
    }
  };

  // For debugging - force a refresh
  const handleManualRefresh = useCallback((): void => {
    if (userOrgId) {
      console.log("Manually refreshing sites data...");
      refetch();
    }
  }, [userOrgId, refetch]);

  // --- Render Logic ---

  // Show loading indicator if auth state is loading or sites query is loading
  if (isAuthLoading || (isSitesLoading && !!userOrgId)) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // No organization assigned
  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-amber-500" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to view sites.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }

  // Handle query error state AFTER loading is complete and userOrgId exists
  if (isError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Sites</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={handleManualRefresh} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Sites</h1>
        <div className="flex gap-2">
          <Button onClick={handleManualRefresh} variant="outline" size="sm" disabled={isSitesLoading}>
            {isSitesLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          {isOrgAdmin && (
            <Button onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" /> Add Site
            </Button>
          )}
        </div>
      </div>
      <p className="text-muted-foreground">
        Manage sites for your organization.
      </p>
      
      {sites?.items?.length === 0 ? (
        <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
          <p className="text-lg">No sites found</p>
          {isOrgAdmin && (
            <Button onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" /> Add Site
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites?.items?.map((site: Site) => (
                <TableRow key={site.id}>
                  <TableCell className="font-medium">{site.name}</TableCell>
                  <TableCell>
                    {site.address_line1}
                    {site.address_line2 ? `, ${site.address_line2}` : ''}
                    <br />
                    {site.city}, {site.state} {site.postal_code}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      site.is_active 
                        ? 'border border-green-400 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400'
                        : 'border border-yellow-400 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400'
                    }`}>
                      {site.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/sites/${site.id}`}>
                        <Button variant="ghost" size="sm">
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                      </Link>
                      {isOrgAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => handleEditClick(site)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      {/* Create Form */}
      <SiteForm
        open={isCreateFormOpen}
        onOpenChange={setIsCreateFormOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createMutation.isPending}
        mode="create"
      />
      
      {/* Edit Form */}
      {currentSite && (
        <SiteForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentSite(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentSite}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}
    </div>
  );
};

export default SitesPage; 